import {ProductType} from '../types';

const parseWeightValue = (weight: ProductType['weight']): number | null => {
  if (weight === null || weight === undefined) {
    return null;
  }

  if (typeof weight === 'number') {
    return Number.isFinite(weight) ? weight : null;
  }

  const parsed = parseFloat(weight);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
};

export const hasPositiveWeight = (weight: ProductType['weight']): boolean => {
  const parsedWeight = parseWeightValue(weight);

  if (parsedWeight === null) {
    return false;
  }

  return parsedWeight > 0;
};

const parseCaloriesValue = (calories: ProductType['calories']): number | null => {
  if (calories === null || calories === undefined) {
    return null;
  }

  if (typeof calories === 'number') {
    return Number.isFinite(calories) ? calories : null;
  }

  if (typeof calories !== 'string') {
    return null;
  }

  const trimmedCalories = calories.trim();

  if (!trimmedCalories) {
    return null;
  }

  const parsed = parseFloat(trimmedCalories);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
};

export const hasPositiveCalories = (
  calories: ProductType['calories'],
): boolean => {
  const parsedCalories = parseCaloriesValue(calories);

  if (parsedCalories === null) {
    return false;
  }

  return parsedCalories > 0;
};

const containsKcalUnit = (value: string) => /kcal/i.test(value);

const normalizeCaloriesLabel = (value: string) => value.replace(/\s+/g, ' ').trim();

type CaloriesLabelOptions = {
  appendUnit?: boolean;
};

export const formatCaloriesLabel = (
  calories: ProductType['calories'],
  options: CaloriesLabelOptions = {},
): string | null => {
  const {appendUnit = true} = options;

  if (!hasPositiveCalories(calories)) {
    return null;
  }

  if (typeof calories === 'number') {
    return appendUnit ? `${calories} kcal` : `${calories}`;
  }

  if (typeof calories !== 'string') {
    return null;
  }

  const normalized = normalizeCaloriesLabel(calories);

  if (!appendUnit) {
    return normalized;
  }

  return containsKcalUnit(normalized) ? normalized : `${normalized} kcal`;
};
